<template>
  <div class="tab-content">
    <div class="counter-display">
      <span>完成焊接次数: {{ count }}</span>
      <button class="reset-counter-btn" @click="resetCounter">重置计数</button>
    </div>

    <div class="main-area">
      <div class="step-content">
        <div class="step">
          <Stepper
            :steps="stepConfigs"
            :current-step="currentStep"
            :is-failed="uiState.isFailed"
          />
          <div class="content">
            <div class="step-panel">
              <div v-if="uiState.isLoading" class="loader"></div>
              <label :class="{ 'fail-label': uiState.isFailed }">{{
                displayMessage
              }}</label>

              <StepForm
                v-if="currentStep === 1"
                v-model="currentStepInputValue"
                :ui-state="uiState"
                @next="handleNext"
                @clear="clearValue"
                @reset="resetWorkflow"
              />
              <div v-else-if="currentStep === 2" class="step-form">
                <div v-if="uiState.isFailed" class="button-list">
                  <button @click="handleNext">重试</button>
                  <button @click="resetWorkflow">重置</button>
                </div>
              </div>
              <div v-else-if="currentStep === 3" class="step-form">
                <div class="button-list">
                  <button
                    v-if="!uiState.isFailed"
                    ref="nextButton"
                    @click="resetWorkflow"
                  >
                    下一个设备
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="uiState.isFailed && uiState.retryTime > 1"
            class="upload-exception"
          >
            <span v-if="uiState.isUploadException" class="click">
              已上报异常
            </span>
            <span v-else @click="reportException">多次重试失败？上报异常</span>
          </div>
        </div>
        <StepMessage ref="messageBox" />
      </div>
    </div>
  </div>
</template>

<script setup>
// ... script 部分与上一版完全相同，此处省略以保持简洁 ...
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import Stepper from "@/components/MyStepper.vue";
import StepForm from "@/components/StepForm.vue";
import StepMessage from "@/components/StepMessage.vue";
import * as validators from "@/utils/validators";
import { useWorkflowCounter } from "@/composables/useWorkflowCounter";

const WELDING_WORKFLOW_CONFIG = {
  type: "welding",
  configs: [
    {
      value: "",
      label: "scanShellCode",
      title: "扫描外壳二维码",
      desc: "请扫描外壳二维码",
      operation: "超声波焊接",
      prompt: "正在校验二维码...",
      enabled: true,
      optional: false,
    },
    {
      value: null,
      label: "checkAfterPowerOn",
      title: "上电后系统检测",
      desc: "请为设备上电，系统将自动检测",
      operation: "超声波焊接",
      prompt: "正在检测设备上行数据...",
      enabled: true,
      optional: false,
    },
    {
      value: null,
      label: "finish",
      title: "完成",
      desc: "检测成功，请开始焊接",
      operation: "超声波焊接",
      prompt: "正在保存数据...",
      enabled: true,
      optional: false,
    },
  ],
};

const store = useStore();
const messageBox = ref(null);
const nextButton = ref(null);

// 防止重复输入的锁和防抖
const isProcessing = ref(false);
let inputDebounceTimer = null;
const DEBOUNCE_DELAY = 300; // 300ms防抖延迟

const stepConfigs = computed(() => store.state.workflow.stepConfigs);
const currentStep = computed(() => store.state.workflow.currentStep);
const uiState = computed(() => store.state.workflow.uiState);
const errResolution = computed(() => store.state.workflow.errorSolution);
const currentStepConfig = computed(
  () => store.getters["workflow/currentStepConfig"]
);
const displayMessage = computed(() => store.getters["workflow/displayMessage"]);
const stepTip = computed(() => store.state.workflow.stepTip);

const currentStepInputValue = computed({
  get: () => currentStepConfig.value.value || "",
  set: (value) =>
    store.commit("workflow/UPDATE_STEP_VALUE", {
      stepIndex: currentStep.value - 1,
      value,
    }),
});
const submitCurrentStep = async () => {
  if (isProcessing.value || uiState.value.isLoading) return;
  isProcessing.value = true;
  try {
    await store.dispatch("workflow/handleCurrentStep");
  } finally {
    if (uiState.value.isFailed || currentStep.value !== 2) {
      isProcessing.value = false;
    }
  }
};
const handleNext = () => submitCurrentStep();
const clearValue = () => {
  currentStepInputValue.value = "";
};
const initWorkflow = () =>
  store.dispatch("workflow/initializeWorkflow", WELDING_WORKFLOW_CONFIG);
const resetWorkflow = () => {
  // 重置时清除处理锁和防抖定时器
  isProcessing.value = false;
  if (inputDebounceTimer) {
    clearTimeout(inputDebounceTimer);
    inputDebounceTimer = null;
  }

  store.dispatch("workflow/resetWorkflow", WELDING_WORKFLOW_CONFIG);
};
const reportException = () => store.dispatch("workflow/uploadException");
const { count, increment, resetCounter } = useWorkflowCounter("welding");

onMounted(() => {
  initWorkflow();
});

watch(
  () => uiState.value.isFailed,
  (isFailed) => {
    // 失败时解除处理锁，允许重试
    if (isFailed) {
      isProcessing.value = false;
      const failType = uiState.value.failType;
      const solution = errResolution.value[failType];
      if (failType && messageBox.value && solution) {
        messageBox.value.show(solution);
      }
    } else {
      const tip = stepTip.value[currentStepConfig.value.label];
      if (messageBox.value && tip) {
        messageBox.value.show(tip);
      } else {
        messageBox.value?.hide();
      }
    }
  }
);
watch(
  currentStep,
  async (newStep, oldStep) => {
    await nextTick();

    // 步骤切换时清空输入框，防止残留数据
    if (newStep !== oldStep && newStep === 1) {
      currentStepInputValue.value = "";
      // 解除处理锁
      isProcessing.value = false;
    }

    if (messageBox.value && !uiState.value.isFailed) {
      const tip = stepTip.value[currentStepConfig.value.label];
      tip ? messageBox.value.show(tip) : messageBox.value.hide();
    }

    // 检查是否到达了"完成"步骤
    if (newStep === 3 && !uiState.value.isFailed) {
      // (为防止重复增加，可能需要加个 ref 锁)
      // 但由于 resetWorkflow 会重置 currentStep，所以这可能是安全的
      increment();

      // 等待 DOM 更新，然后自动聚焦到按钮上
      await nextTick();
      if (nextButton.value) {
        nextButton.value.focus();
      }
    }
  },
  { immediate: true }
);
watch(
  currentStepInputValue,
  (newValue, oldValue) => {
    // 清除之前的防抖定时器
    if (inputDebounceTimer) {
      clearTimeout(inputDebounceTimer);
      inputDebounceTimer = null;
    }

    let value = newValue;
    if (!value) {
      isProcessing.value = false;
      return;
    }

    // 如果正在处理中，忽略新输入
    if (isProcessing.value) {
      console.warn("正在处理中，忽略重复输入:", value);
      return;
    }

    // 防抖处理：延迟执行验证
    inputDebounceTimer = setTimeout(() => {
      const { isValid } = validators.validateShellDeviceEUI(value);

      if (isValid) {
        submitCurrentStep();
      }
    }, DEBOUNCE_DELAY);
  },
  { immediate: true }
);
</script>

<style scoped>
.main-area {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* 计数器样式 */
.counter-display {
  position: absolute;
  bottom: 0.5rem; /* 调整到 .content-area padding 内 */
  left: 1.5rem; /* 调整到 .content-area padding 内 */
  background: rgba(240, 245, 255, 0.9);
  padding: 5px 10px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 10;
  font-size: 0.9rem;
}
.reset-counter-btn {
  background: #f56c6c;
  color: white;
  border: none;
  padding: 2px 5px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 0.8rem;
}
.reset-counter-btn:hover {
  background: #f89898;
}
</style>
